// Fully synthetic demo fixtures for the Inbound Calls mock.
// Every name, phone number, and transcript here is invented — never copied
// from production calls or recordings. Caller/patient names were AI-generated
// and hardcoded (see roster in DEMO_CALLS below).
//
// Fixture invariants (enforced by tests):
// - Every transcript opens with the recording disclosure line.
// - Every call is attributed to a practice, including the unresolved one:
//   attribution comes from the dialed line, not from what the caller said.
// - Every knowledgeEntryId on a turn resolves to a knowledge bank entry, and a
//   PRACTICE-tier citation always belongs to that call's own practice.
// - Neither tier says which insurance plans are accepted, so payer questions
//   are always answered with "not sure, someone will call back".
// - No spoken turn says "knowledge bank" — that is dashboard vocabulary. On a
//   call the assistant declines the way a receptionist would. Recaps, which
//   only staff read, still name the bank.
// - The agent never proactively discloses being an AI; exactly one call
//   contains an honest answer when the caller asks directly.
// - APPOINTMENT_SCHEDULED means the assistant booked the appointment
//   directly. APPOINTMENT_REQUESTED represents cancel-appointment requests,
//   since scheduling is handled by software but deletion is not.

import {
  DemoPractice,
  InboundCall,
  InboundLineContext,
  KnowledgeEntry,
  TranscriptTurn,
} from "./types";

/** Explicit demo timezone so day buckets never shift with the viewer's browser timezone. */
export const INBOUND_DEMO_TIMEZONE = "America/New_York";

export const RECORDING_DISCLOSURE_LINE =
  "Thank you for calling Gracent Cares. This call may be recorded for quality and training purposes.";

const GREETING = `${RECORDING_DISCLOSURE_LINE} How can I help you today?`;

export const DEMO_PRACTICES: DemoPractice[] = [
  {
    id: "practice-riverside",
    name: "Gracent Cares — Riverside",
    shortName: "Riverside",
    servicesOffered: ["ABA Therapy", "Speech Therapy", "Occupational Therapy"],
  },
  {
    id: "practice-oakhill",
    name: "Gracent Cares — Oak Hill",
    shortName: "Oak Hill",
    servicesOffered: ["Speech Therapy", "Psychotherapy"],
  },
];

/** Every call carries a practice, so this always resolves to a display name. */
export function practiceNameById(practiceId: string): string {
  return DEMO_PRACTICES.find((p) => p.id === practiceId)?.name ?? practiceId;
}

/** "Riverside" rather than "Gracent Cares — Riverside", for tight labels. */
export function practiceShortNameById(practiceId: string): string {
  return DEMO_PRACTICES.find((p) => p.id === practiceId)?.shortName ?? practiceId;
}

/**
 * The two-tier knowledge bank the assistant is allowed to answer from:
 * organization-wide entries plus optional per-practice overlays. Anything not
 * in here is a follow-up task, never an improvised answer.
 */
export const DEMO_KNOWLEDGE_ENTRIES: KnowledgeEntry[] = [
  {
    id: "org-kb-001",
    tier: "ORG",
    question: "How long is the ABA program?",
    answer:
      "A typical ABA program at Gracent Cares runs 6 to 12 months, with the exact length set after the initial evaluation. Sessions are usually 2 to 4 times per week.",
    lastReviewedAt: "2026-07-14",
  },
  {
    id: "org-kb-003",
    tier: "ORG",
    question: "What are your office hours?",
    answer:
      "Gracent Cares locations are open Monday through Friday, 8 AM to 6 PM. The after-hours line takes messages and answers common questions outside those hours.",
    lastReviewedAt: "2026-06-30",
  },
  {
    id: "org-kb-004",
    tier: "ORG",
    question: "How do I request records or forms?",
    answer:
      "Intake forms are on the patient portal and the front desk can email them during business hours. Records requests need a signed release form and take 5 to 7 business days to process.",
    lastReviewedAt: "2026-07-22",
  },
  {
    id: "org-kb-005",
    tier: "ORG",
    question: "What ages do you serve?",
    answer:
      "Gracent Cares serves patients from 18 months through 21 years across ABA, speech, and occupational therapy. Psychotherapy at Oak Hill is also available to adult caregivers.",
    lastReviewedAt: "2026-07-08",
  },
  {
    id: "org-kb-006",
    tier: "ORG",
    question: "Do you offer virtual or teletherapy sessions?",
    answer:
      "Speech therapy is offered both in person and by teletherapy. ABA and occupational therapy are in person only, because both require hands-on work.",
    lastReviewedAt: "2026-08-05",
  },
  {
    id: "org-kb-007",
    tier: "ORG",
    question: "Is a physician referral required to start?",
    answer:
      "A physician referral is required for ABA services and for insurance-funded speech therapy. Private-pay speech and occupational therapy evaluations do not require one.",
    lastReviewedAt: "2026-07-29",
  },
  {
    id: "org-kb-008",
    tier: "ORG",
    question: "What should we bring to the first visit?",
    answer:
      "Bring the insurance card, photo ID, any prior evaluation reports, and the completed intake packet. Arriving 15 minutes early gives the front desk time to verify benefits.",
    lastReviewedAt: "2026-07-18",
  },
  {
    id: "org-kb-009",
    tier: "ORG",
    question: "How long is the wait for an initial evaluation?",
    answer:
      "Initial evaluations are typically scheduled 2 to 4 weeks out. Cancellation slots often open sooner, and the front desk keeps a short-notice list.",
    lastReviewedAt: "2026-08-10",
  },
  {
    id: "prac-riverside-kb-001",
    tier: "PRACTICE",
    practiceId: "practice-riverside",
    question: "Does Riverside offer occupational therapy?",
    answer:
      "The Riverside location offers occupational therapy in addition to ABA and speech therapy. Evaluations are scheduled through the front desk during business hours.",
    lastReviewedAt: "2026-08-02",
  },
  {
    id: "prac-riverside-kb-002",
    tier: "PRACTICE",
    practiceId: "practice-riverside",
    question: "Where do families park at Riverside?",
    answer:
      "Riverside has a dedicated patient lot on the north side of the building, with two accessible spaces by the main entrance. Street parking is not metered after 5 PM.",
    lastReviewedAt: "2026-07-25",
  },
  {
    id: "prac-oakhill-kb-001",
    tier: "PRACTICE",
    practiceId: "practice-oakhill",
    question: "Does Oak Hill offer psychotherapy?",
    answer:
      "Oak Hill offers psychotherapy in addition to speech therapy. ABA services are not offered at Oak Hill — those are at the Riverside location.",
    lastReviewedAt: "2026-08-03",
  },
  {
    id: "prac-oakhill-kb-002",
    tier: "PRACTICE",
    practiceId: "practice-oakhill",
    question: "Does Oak Hill have Saturday availability?",
    answer:
      "Oak Hill runs a limited Saturday morning clinic twice a month for speech therapy follow-ups. New evaluations are weekdays only.",
    lastReviewedAt: "2026-07-30",
  },
];

/**
 * Throws rather than returning undefined: a transcript citing an entry that
 * does not exist is a fixture bug, not a state the UI should try to render.
 */
export function knowledgeEntryById(entryId: string): KnowledgeEntry {
  const entry = DEMO_KNOWLEDGE_ENTRIES.find((e) => e.id === entryId);
  if (!entry) {
    throw new Error(`Unknown knowledge bank entry: ${entryId}`);
  }
  return entry;
}

/** Human-readable source line shown when a cited reply is expanded. */
export function knowledgeSourceLabel(entry: KnowledgeEntry): string {
  if (entry.tier === "PRACTICE") {
    return `Used information from the ${practiceNameById(
      entry.practiceId
    )} practice knowledge bank`;
  }
  return "Used information from the organization-wide knowledge bank";
}

/**
 * The plain description of each group of answers, shown on the knowledge bank
 * page and editable there. Each one must still name every topic its group can
 * be cited for: an answer the description does not account for reads as
 * invented to anyone checking the call it was used on.
 */
export const DEMO_LINE_CONTEXT: InboundLineContext = {
  orgSummary:
    "Gracent Cares offers ABA therapy and speech therapy at every location. " +
    "These answers cover office hours, the ages we treat (18 months to 21 " +
    "years), how long a program runs, when a doctor's referral is needed, " +
    "which sessions can be done online, what to bring to a first visit, how " +
    "to get forms and records, and how long the wait is for a first " +
    "appointment.",
  perPracticeSummaries: [
    {
      practiceId: "practice-riverside",
      summary:
        "Riverside also offers occupational therapy. These answers cover " +
        "that and where families park. Everything else a Riverside caller " +
        "asks is answered from the all-locations list.",
    },
    {
      practiceId: "practice-oakhill",
      summary:
        "Oak Hill also offers psychotherapy, and ABA therapy is not offered " +
        "there. These answers cover that and the Saturday morning speech " +
        "clinic held twice a month. Everything else an Oak Hill caller asks " +
        "is answered from the all-locations list.",
    },
  ],
  notCoveredNote:
    "Insurance coverage isn’t included, so those callers get a callback.",
  // Weeknight evenings plus weekend daytime, authored a day at a time so the
  // sample shows what per-day coverage looks like rather than one blanket rule.
  coverageSchedule: {
    MON: { allDay: false, windows: [{ start: "17:00", end: "22:00" }] },
    TUE: { allDay: false, windows: [{ start: "17:00", end: "22:00" }] },
    WED: { allDay: false, windows: [{ start: "17:00", end: "22:00" }] },
    THU: { allDay: false, windows: [{ start: "17:00", end: "22:00" }] },
    FRI: { allDay: false, windows: [{ start: "17:00", end: "22:00" }] },
    SAT: { allDay: false, windows: [{ start: "09:00", end: "17:00" }] },
    SUN: { allDay: false, windows: [{ start: "09:00", end: "17:00" }] },
  },
  timezone: INBOUND_DEMO_TIMEZONE,
  timezoneLabel: "Eastern time",
};

/**
 * Topics callers actually raised in the fixtures that no entry covers. Listed
 * on the knowledge bank view so the boundary of the bank is visible alongside
 * its contents: each of these becomes a message for staff, never an answer.
 */
export const DEMO_KNOWLEDGE_GAPS: string[] = [
  "Which insurance plans are accepted, and coverage for a specific plan",
  "Whether an individual provider is taking new patients",
  "The attendance and cancellation policy for missed sessions",
  "Whether psychotherapy can be delivered virtually",
  "How completed records are delivered (email or paper)",
  "Anything account-specific: invoices, existing appointments, records",
];

/** [speaker, text, offsetSeconds, knowledgeEntryId?] */
type TurnTuple = [TranscriptTurn["speaker"], string, number, string?];

const turns = (entries: TurnTuple[]): TranscriptTurn[] =>
  entries.map(([speaker, text, offsetSeconds, knowledgeEntryId]) => ({
    speaker,
    text,
    offsetSeconds,
    ...(knowledgeEntryId === undefined ? {} : { knowledgeEntryId }),
  }));

export const DEMO_CALLS: InboundCall[] = [
  // ---------- Monday 2026-08-17 ----------
  {
    id: "c-001",
    receivedAt: "2026-08-17T18:12:00-04:00",
    practiceId: "practice-riverside",
    callerName: "Elena Voss",
    callerPhone: "(555) 014-2231",
    patientName: "Milo Voss",
    callerRelationship: "Mother",
    reason: "SCHEDULING",
    recapText:
      "Elena Voss needs to cancel Milo's upcoming ABA session this Thursday at Riverside due to a family conflict. Asked about the cancellation policy — not in the knowledge bank, noted for staff. Office hours confirmed from the knowledge bank.",
    outcome: "APPOINTMENT_REQUESTED",
    nextStep:
      "Front desk to cancel Milo Voss's Thursday ABA session at Riverside and confirm with Elena.",
    followUpTask: {
      description: "Cancel Milo Voss's Thursday ABA session at Riverside per Elena's request.",
      patientInfoCollected: [
        "Patient: Milo Voss, age 5",
        "Parent/caller: Elena Voss (mother)",
        "Request: cancel this Thursday's ABA session",
        "Reason: family conflict — out of town",
        "Asked about the cancellation policy — needs a staff answer",
      ],
      callbackNumber: "(555) 014-2231",
      dueBy: "2026-08-18T12:00:00-04:00",
      status: "PENDING",
    },
    transcript: turns([
      ["AGENT", GREETING, 0],
      ["CALLER", "Hi, I need to cancel my son Milo's ABA session this Thursday. We have a family thing and we'll be out of town.", 6],
      ["AGENT", "I can pass that along to the front desk so they can cancel Thursday's session. Can I confirm your callback number in case they need to reach you?", 14],
      ["CALLER", "Sure, it's 555-014-2231.", 24],
      ["AGENT", "Thank you. Is there anything else I can help with?", 30],
      ["CALLER", "Actually — is there a cancellation fee or anything like that? I don't want a surprise charge.", 36],
      ["AGENT", "I don't have the attendance or cancellation policy in front of me, so I don't want to guess. The front desk can walk you through that when they confirm the cancellation.", 44],
      ["CALLER", "Okay. And when will they actually process it? We're leaving Wednesday night.", 58],
      ["AGENT", "Our locations are open Monday through Friday, 8 AM to 6 PM, so someone should be able to take care of it tomorrow morning.", 66, "org-kb-003"],
      ["CALLER", "That works.", 78],
      ["AGENT", "To confirm: cancelling Milo's ABA session this Thursday at Riverside, callback at 555-014-2231. The front desk will confirm by tomorrow.", 82],
      ["CALLER", "Perfect, thank you.", 96],
    ]),
  },
  {
    id: "c-002",
    receivedAt: "2026-08-17T19:45:00-04:00",
    practiceId: "practice-riverside",
    callerName: "Marcus Chen",
    callerPhone: "(555) 028-4410",
    reason: "THERAPY_LOGISTICS",
    recapText:
      "Marcus Chen asked how long the ABA program runs, whether sessions can be virtual, what ages are served, and whether a referral is needed. All four answered from the organization knowledge bank.",
    outcome: "ANSWERED_FROM_KNOWLEDGE",
    nextStep: "None — question fully answered from the knowledge bank.",
    transcript: turns([
      ["AGENT", GREETING, 0],
      ["CALLER", "How long does the ABA program usually run?", 5],
      ["AGENT", "A typical ABA program at Gracent Cares runs 6 to 12 months, with the exact length set after the initial evaluation. Sessions are usually 2 to 4 times per week.", 11, "org-kb-001"],
      ["CALLER", "That's longer than I expected. Is any of it virtual?", 24],
      ["AGENT", "Speech therapy is offered both in person and by teletherapy, but ABA and occupational therapy are in person only — both require hands-on work.", 31, "org-kb-006"],
      ["CALLER", "Okay. And what ages do you take? My nephew is almost three.", 45],
      ["AGENT", "We serve patients from 18 months through 21 years across ABA, speech, and occupational therapy, so he's well within range.", 53, "org-kb-005"],
      ["CALLER", "Good. Anything else I should know before we call to book?", 66],
      ["AGENT", "A physician referral is required for ABA and for insurance-funded speech therapy. Private-pay speech and occupational therapy evaluations don't need one.", 72, "org-kb-007"],
      ["CALLER", "Okay, that's what I needed. Thanks.", 86],
      ["AGENT", "You're welcome. Anything else I can help with?", 90],
      ["CALLER", "No, that's all.", 94],
    ]),
  },

  // ---------- Tuesday 2026-08-18 ----------
  {
    id: "c-003",
    receivedAt: "2026-08-18T18:30:00-04:00",
    practiceId: "practice-oakhill",
    callerName: "Simone Delacroix",
    callerPhone: "(555) 031-7788",
    reason: "INSURANCE_ACCEPTANCE",
    recapText:
      "Simone Delacroix asked whether Gracent Cares accepts Aetna. Which plans are accepted is not in the knowledge bank at either tier, so the assistant said it wasn't sure and took her details for a callback. Saturday availability at Oak Hill and first-visit items were answered from the knowledge bank.",
    outcome: "FOLLOW_UP_NEEDED",
    nextStep:
      "Insurance team to confirm whether Aetna is accepted for speech therapy and call Simone back.",
    followUpTask: {
      description:
        "Confirm whether Aetna is accepted for speech therapy at Oak Hill and call Simone Delacroix back.",
      patientInfoCollected: [
        "Caller: Simone Delacroix (prospective patient)",
        "Insurance: Aetna (plan type unknown)",
        "Interested in: speech therapy at Oak Hill",
        "Weekdays are difficult — asked about Saturday follow-up slots",
      ],
      callbackNumber: "(555) 031-7788",
      dueBy: "2026-08-19T11:00:00-04:00",
      status: "PENDING",
    },
    transcript: turns([
      ["AGENT", GREETING, 0],
      ["CALLER", "Do you guys take Aetna?", 5],
      ["AGENT", "I'm not sure, honestly — which insurance plans we accept isn't something I can confirm from here, and I don't want to guess. I can have our team check and call you back, usually within a few hours of the office opening.", 9],
      ["CALLER", "That's fine. It'd be for speech, at the Oak Hill office.", 26],
      ["AGENT", "Oak Hill does offer speech therapy, so that part works. Can I get your name and a callback number for the insurance question?", 34, "prac-oakhill-kb-001"],
      ["CALLER", "Simone Delacroix, 555-031-7788.", 46],
      ["AGENT", "Thank you, Simone. Anything else I can help with while you're on?", 53],
      ["CALLER", "One thing — we both work weekdays. Do you ever do Saturdays there?", 60],
      ["AGENT", "Oak Hill runs a limited Saturday morning clinic twice a month, but it's for speech therapy follow-ups. New evaluations are weekdays only.", 69, "prac-oakhill-kb-002"],
      ["CALLER", "So the first appointment has to be a weekday.", 82],
      ["AGENT", "That's right. The evaluation itself is on a weekday, and follow-ups may be able to move to a Saturday slot after that.", 87],
      ["CALLER", "That's workable. What do we bring to the first one?", 99],
      ["AGENT", "Your insurance card, photo ID, any prior evaluation reports, and the completed intake packet. Arriving fifteen minutes early gives the front desk time to verify benefits.", 105, "org-kb-008"],
      ["CALLER", "Great, thank you!", 119],
      ["AGENT", "You're welcome. Someone will get back to you on the Aetna question — expect a call within a few hours of the office opening tomorrow.", 123],
    ]),
  },
  {
    id: "c-004",
    receivedAt: "2026-08-18T20:05:00-04:00",
    practiceId: "practice-riverside",
    callerName: "Garrett Fenwick",
    callerPhone: "(555) 042-9083",
    patientName: "Garrett Fenwick",
    callerRelationship: "Self",
    reason: "BILLING_INVOICING",
    recapText:
      "Garrett Fenwick has a question about a charge on invoice #4417 from July. Account-specific — collected details for the billing team and confirmed callback hours from the knowledge bank.",
    outcome: "FOLLOW_UP_NEEDED",
    nextStep: "Billing team to review invoice #4417 and call Garrett back.",
    followUpTask: {
      description: "Review invoice #4417 (July) and call Garrett Fenwick back about the disputed charge.",
      patientInfoCollected: [
        "Patient: Garrett Fenwick (self)",
        "Invoice referenced: #4417, July statement",
        "Concern: two sessions billed in the second week of July, caller recalls one",
        "Preferred callback: after 4 PM",
      ],
      callbackNumber: "(555) 042-9083",
      dueBy: "2026-08-19T17:00:00-04:00",
      status: "PENDING",
    },
    transcript: turns([
      ["AGENT", GREETING, 0],
      ["CALLER", "I got an invoice, number 4417, and one of the charges looks way too high.", 6],
      ["AGENT", "I'm not able to look up account details on this line, but I can take the information and have our billing team call you back. Can I get your name and a callback number?", 15],
      ["CALLER", "Garrett Fenwick, 555-042-9083.", 28],
      ["AGENT", "Thank you. Do you know roughly which date of service the charge is for?", 34],
      ["CALLER", "I think the second week of July. There are two sessions listed and I only remember one.", 41],
      ["AGENT", "That's helpful — I've noted that the July statement shows two sessions and you're questioning the second one.", 51],
      ["CALLER", "Right. And when can I expect someone? I work during the day.", 62],
      ["AGENT", "Our locations are open Monday through Friday, 8 AM to 6 PM, and this line covers the hours outside that. The billing team will call within business hours, so I can note that later in the day is better for you.", 69, "org-kb-003"],
      ["CALLER", "Yes, after four if possible.", 88],
      ["AGENT", "Noted. The billing team will review invoice 4417 and call you back by end of day tomorrow at 555-042-9083, after 4 PM where possible.", 93],
      ["CALLER", "Alright, I appreciate it.", 108],
    ]),
  },
  {
    id: "c-005",
    receivedAt: "2026-08-18T21:15:00-04:00",
    practiceId: "practice-riverside",
    callerName: "Anika Mehta",
    callerPhone: "(555) 057-3345",
    patientName: "Rohan Mehta",
    callerRelationship: "Mother",
    reason: "PROVIDER_QUESTION",
    recapText:
      "Anika Mehta asked whether Dr. Johnson at Riverside is taking new patients. No knowledge bank answer available — collected details for a staff callback, and answered her referral and teletherapy questions from the knowledge bank. Caller asked if the assistant was an AI; the assistant confirmed honestly.",
    outcome: "FOLLOW_UP_NEEDED",
    nextStep: "Riverside front desk to confirm Dr. Johnson's availability and call Anika back.",
    followUpTask: {
      description: "Confirm whether Dr. Johnson is accepting new patients and call Anika Mehta back.",
      patientInfoCollected: [
        "Prospective patient: Rohan Mehta, age 7",
        "Caller: Anika Mehta (mother)",
        "Interested in: speech therapy with Dr. Johnson",
        "Plans to use insurance — referral needed",
        "Interested in teletherapy sessions",
      ],
      callbackNumber: "(555) 057-3345",
      dueBy: "2026-08-19T12:00:00-04:00",
      status: "PENDING",
    },
    transcript: turns([
      ["AGENT", GREETING, 0],
      ["CALLER", "Is Dr. Johnson at your Riverside office taking new patients?", 6],
      ["AGENT", "I don't have individual providers' schedules in front of me, so I don't want to guess. I can take your details and have the Riverside team call you back with an answer.", 13],
      ["CALLER", "Wait — am I talking to a robot?", 26],
      ["AGENT", "Yes — I'm an automated assistant that answers after-hours calls for Gracent Cares. A staff member will handle your callback personally.", 30],
      ["CALLER", "Huh, okay. It's for my son Rohan, he's seven, we're looking for speech therapy. My number is 555-057-3345.", 40],
      ["AGENT", "Thank you. While you're on, is there anything else I can help with in the meantime?", 52],
      ["CALLER", "Sure — does he need a referral for speech?", 61],
      ["AGENT", "If it's funded through insurance, yes — a physician referral is required for speech therapy, same as for ABA. Private-pay evaluations don't need one.", 66, "org-kb-007"],
      ["CALLER", "We'd be going through insurance. Could the sessions be online?", 82],
      ["AGENT", "Speech therapy is offered both in person and by teletherapy, so online sessions are an option once he's established.", 90, "org-kb-006"],
      ["CALLER", "That would help a lot, we're a bit of a drive out.", 102],
      ["AGENT", "I've added both notes to the message. The Riverside team will confirm Dr. Johnson's availability and call you back by midday tomorrow.", 108],
    ]),
  },

  // ---------- Wednesday 2026-08-19 ----------
  {
    id: "c-006",
    receivedAt: "2026-08-19T17:55:00-04:00",
    practiceId: "practice-riverside",
    callerName: "Devon Price",
    callerPhone: "(555) 063-1102",
    reason: "THERAPY_LOGISTICS",
    recapText:
      "Devon Price asked whether Riverside offers occupational therapy, where to park, and when the front desk opens. Answered from Riverside's practice overlay and the organization knowledge bank.",
    outcome: "ANSWERED_FROM_KNOWLEDGE",
    nextStep: "None — question fully answered from the knowledge bank.",
    transcript: turns([
      ["AGENT", GREETING, 0],
      ["CALLER", "Does your Riverside office do occupational therapy?", 5],
      ["AGENT", "Yes — the Riverside location offers occupational therapy in addition to ABA and speech therapy. Evaluations are scheduled through the front desk during business hours.", 10, "prac-riverside-kb-001"],
      ["CALLER", "Perfect. Is that the building off the main road? I'm never sure where to park.", 23],
      ["AGENT", "Riverside has a dedicated patient lot on the north side of the building, with two accessible spaces by the main entrance. Street parking isn't metered after 5 PM.", 32, "prac-riverside-kb-002"],
      ["CALLER", "That's helpful. And what time does the front desk open?", 48],
      ["AGENT", "Locations are open Monday through Friday, 8 AM to 6 PM. This after-hours line covers evenings and weekends.", 54, "org-kb-003"],
      ["CALLER", "Perfect, I'll call back tomorrow then.", 67],
      ["AGENT", "Sounds good. Anything else before you go?", 72],
      ["CALLER", "No, that's it. Thanks.", 76],
    ]),
  },
  {
    id: "c-007",
    receivedAt: "2026-08-19T19:20:00-04:00",
    practiceId: "practice-oakhill",
    callerName: "Yuki Nakamura",
    callerPhone: "(555) 071-6650",
    reason: "RECORDS_FORMS",
    recapText:
      "Yuki Nakamura asked how to get new-patient intake forms, what to bring, and whether a referral is needed. All answered from the organization knowledge bank.",
    outcome: "ANSWERED_FROM_KNOWLEDGE",
    nextStep: "None — question fully answered from the knowledge bank.",
    transcript: turns([
      ["AGENT", GREETING, 0],
      ["CALLER", "I need the intake forms for a new patient. Where do I get those?", 6],
      ["AGENT", "Intake forms are on the Gracent Cares patient portal, and the front desk can email them during business hours. Records requests are separate — those need a signed release form and take 5 to 7 business days.", 13, "org-kb-004"],
      ["CALLER", "The portal works. Do they need to be done before we come in?", 30],
      ["AGENT", "Yes — bring the completed intake packet along with your insurance card, photo ID, and any prior evaluation reports. Arriving fifteen minutes early gives the front desk time to verify benefits.", 38, "org-kb-008"],
      ["CALLER", "Got it. Is there a referral involved too?", 54],
      ["AGENT", "That depends on the service. A referral is required for ABA and for insurance-funded speech therapy, but not for private-pay speech or occupational therapy evaluations.", 60, "org-kb-007"],
      ["CALLER", "It's speech, through insurance.", 76],
      ["AGENT", "Then you'll want the referral from your physician as well. The front desk can confirm what's already on file when you come in.", 81],
      ["CALLER", "Great. Thanks!", 94],
    ]),
  },

  // ---------- Thursday 2026-08-20 ----------
  {
    id: "c-009",
    receivedAt: "2026-08-20T20:50:00-04:00",
    practiceId: "practice-oakhill",
    callerName: "Carmen Ruiz",
    callerPhone: "(555) 094-5561",
    patientName: "Lucia Ruiz",
    callerRelationship: "Mother",
    reason: "SCHEDULING",
    recapText:
      "Carmen Ruiz needs to cancel Lucia's speech therapy session next Tuesday at Oak Hill — Lucia is sick. Asked about Saturday availability for a make-up, answered from the knowledge bank. Left callback number.",
    outcome: "APPOINTMENT_REQUESTED",
    nextStep: "Oak Hill front desk to cancel Lucia's Tuesday speech session and confirm with Carmen.",
    followUpTask: {
      description: "Cancel Lucia Ruiz's Tuesday speech therapy session at Oak Hill per Carmen's request.",
      patientInfoCollected: [
        "Patient: Lucia Ruiz, age 4",
        "Parent/caller: Carmen Ruiz (mother)",
        "Request: cancel next Tuesday's speech therapy session",
        "Reason: Lucia is sick",
        "Interested in rescheduling to a Saturday if available",
      ],
      callbackNumber: "(555) 094-5561",
      dueBy: "2026-08-21T12:00:00-04:00",
      status: "PENDING",
    },
    transcript: turns([
      ["AGENT", GREETING, 0],
      ["CALLER", "I need to cancel my daughter Lucia's speech session next Tuesday at Oak Hill. She's come down with something and I don't want to bring her in sick.", 6],
      ["AGENT", "I'll pass that along to the Oak Hill front desk so they can cancel Tuesday's session. Can I get your callback number?", 16],
      ["CALLER", "It's 555-094-5561.", 28],
      ["AGENT", "Thank you. Is there anything else I can help with?", 34],
      ["CALLER", "Actually — could we reschedule to a Saturday instead? Weekday mornings are getting harder now that she's starting pre-K.", 40],
      ["AGENT", "Oak Hill runs a limited Saturday morning clinic twice a month, but it's for speech therapy follow-ups — so that could work since Lucia is an established patient. The front desk can check for an open Saturday slot when they confirm the cancellation.", 52, "prac-oakhill-kb-002"],
      ["CALLER", "That would be great.", 70],
      ["AGENT", "To confirm: cancelling Lucia's speech session next Tuesday at Oak Hill, callback at 555-094-5561, with a note to look at Saturday availability for a make-up. The front desk will confirm by tomorrow.", 76],
      ["CALLER", "Sounds good, thanks.", 94],
    ]),
  },

  // ---------- Friday 2026-08-21 ----------
  {
    id: "c-010",
    receivedAt: "2026-08-21T18:05:00-04:00",
    practiceId: "practice-riverside",
    callerName: "Garrett Fenwick",
    callerPhone: "(555) 042-9083",
    patientName: "Garrett Fenwick",
    callerRelationship: "Self",
    reason: "BILLING_INVOICING",
    recapText:
      "Garrett Fenwick called again about invoice #4417 — has not yet received the promised billing callback. Flagged as urgent for the billing team.",
    outcome: "FOLLOW_UP_NEEDED",
    nextStep: "Billing team to call Garrett back first thing next business day (second contact, overdue).",
    followUpTask: {
      description: "URGENT: second call about invoice #4417 — original callback not completed. Call Garrett Fenwick back first thing.",
      patientInfoCollected: [
        "Patient: Garrett Fenwick (self)",
        "Invoice referenced: #4417, July statement",
        "Second call on this issue — first callback missed",
        "Wants a direct extension left on voicemail if unreachable",
      ],
      callbackNumber: "(555) 042-9083",
      dueBy: "2026-08-24T10:00:00-04:00",
      status: "PENDING",
    },
    transcript: turns([
      ["AGENT", GREETING, 0],
      ["CALLER", "I called earlier this week about invoice 4417 and nobody has called me back.", 6],
      ["AGENT", "I'm sorry about that. I've flagged this as urgent so the billing team calls you back first thing on the next business day at 555-042-9083. Is that still the best number?", 14],
      ["CALLER", "Yes it is. Please make sure it happens this time.", 28],
      ["AGENT", "Understood — it's marked as a priority follow-up, with a note that the first callback was missed.", 34],
      ["CALLER", "What time do they actually start? I don't want to miss it again.", 46],
      ["AGENT", "The offices open at 8 AM Monday through Friday and close at 6 PM. I've asked for first thing, so expect the call shortly after eight.", 53, "org-kb-003"],
      ["CALLER", "Okay. And if I do miss it, is there a direct number?", 68],
      ["AGENT", "The billing team will leave a voicemail with a direct extension if they can't reach you, so you won't have to come back through this line.", 75],
      ["CALLER", "Alright, thank you.", 89],
    ]),
  },
  {
    id: "c-011",
    receivedAt: "2026-08-21T21:30:00-04:00",
    practiceId: "practice-oakhill",
    callerName: "Unknown caller",
    callerPhone: "(555) 099-0000",
    reason: "OTHER",
    recapText:
      "Caller disconnected after the greeting before stating a need. The assistant re-prompted once and no information was collected.",
    outcome: "UNRESOLVED",
    nextStep: "None possible — no caller details captured. Visible here so no call is silently dropped.",
    transcript: turns([
      ["AGENT", GREETING, 0],
      ["AGENT", "Hello — I'm not picking up any speech on the line. If you can hear me, you're welcome to call back at any time.", 9],
      ["CALLER", "(call disconnected)", 17],
    ]),
  },

  // ---------- Saturday 2026-08-22 (weekend) ----------
  {
    id: "c-012",
    receivedAt: "2026-08-22T10:15:00-04:00",
    practiceId: "practice-oakhill",
    callerName: "Renata Okonkwo",
    callerPhone: "(555) 103-4478",
    reason: "THERAPY_LOGISTICS",
    recapText:
      "Renata Okonkwo asked whether Oak Hill offers psychotherapy and whether adult sessions can be virtual. Service question answered from Oak Hill's overlay; the assistant declined to guess on virtual psychotherapy.",
    outcome: "ANSWERED_FROM_KNOWLEDGE",
    nextStep: "None — caller declined a callback and will ask when she calls the office.",
    transcript: turns([
      ["AGENT", GREETING, 0],
      ["CALLER", "Does the Oak Hill location offer psychotherapy, or just the kids' therapy stuff?", 5],
      ["AGENT", "Oak Hill offers psychotherapy in addition to speech therapy. ABA services are not offered at Oak Hill — those are at our Riverside location.", 12, "prac-oakhill-kb-001"],
      ["CALLER", "Is the psychotherapy just for kids, or for adults too?", 24],
      ["AGENT", "We serve patients from 18 months through 21 years across therapy services, and psychotherapy at Oak Hill is also available to adult caregivers.", 31, "org-kb-005"],
      ["CALLER", "That's actually what I was asking about — it'd be for me, alongside my daughter's speech sessions.", 46],
      ["AGENT", "That combination works at Oak Hill, since both services are offered on site.", 57],
      ["CALLER", "Could my sessions be virtual?", 66],
      ["AGENT", "Teletherapy is offered for speech therapy. Whether psychotherapy can be virtual isn't something I can confirm, so I'd rather have the Oak Hill team answer that than guess.", 71, "org-kb-006"],
      ["CALLER", "Fair enough, I'll ask when I call them.", 88],
      ["AGENT", "Sounds good. Anything else I can help with?", 93],
      ["CALLER", "No — got it, thanks.", 96],
    ]),
  },
  {
    id: "c-013",
    receivedAt: "2026-08-22T13:40:00-04:00",
    practiceId: "practice-riverside",
    callerName: "Ibrahim Saleh",
    callerPhone: "(555) 110-8890",
    patientName: "Noor Saleh",
    callerRelationship: "Father",
    reason: "SCHEDULING",
    recapText:
      "Ibrahim Saleh wants to cancel Noor's remaining speech sessions at Riverside — the family is relocating. Asked about records transfer and parking for a final visit. Parking answered from Riverside's overlay; records process from the knowledge bank.",
    outcome: "APPOINTMENT_REQUESTED",
    nextStep: "Riverside front desk to cancel Noor Saleh's upcoming speech sessions and confirm with Ibrahim.",
    followUpTask: {
      description: "Cancel Noor Saleh's remaining speech sessions at Riverside — family relocating.",
      patientInfoCollected: [
        "Patient: Noor Saleh",
        "Caller: Ibrahim Saleh (father)",
        "Request: cancel all remaining speech sessions",
        "Reason: family is relocating out of the area",
        "Also asked about transferring records to a new provider",
      ],
      callbackNumber: "(555) 110-8890",
      dueBy: "2026-08-24T12:00:00-04:00",
      status: "PENDING",
    },
    transcript: turns([
      ["AGENT", GREETING, 0],
      ["CALLER", "I need to cancel my daughter Noor's speech sessions. We're moving out of state next month.", 6],
      ["AGENT", "I'll pass that along to the Riverside front desk so they can cancel the remaining sessions. Best callback number?", 14],
      ["CALLER", "555-110-8890.", 24],
      ["AGENT", "Thank you. Would you like to cancel all sessions going forward, or keep any that fall before the move?", 28],
      ["CALLER", "Cancel everything. We leave in two weeks so it's not worth squeezing one in.", 38],
      ["AGENT", "Understood — I've noted to cancel all remaining sessions.", 46],
      ["CALLER", "One more thing — can we get Noor's records sent to a new therapist once we find one?", 52],
      ["AGENT", "Absolutely. Records requests go through the front desk with a signed release form — you can download the form from the patient portal or pick one up at any location. Processing usually takes 5 to 7 business days.", 60, "org-kb-004"],
      ["CALLER", "We might swing by to pick up the form. Where do we park again?", 78],
      ["AGENT", "Riverside's patient lot is on the north side of the building, with two accessible spaces by the main entrance. Street parking isn't metered after 5 PM.", 85, "prac-riverside-kb-002"],
      ["CALLER", "Got it, thanks.", 99],
      ["AGENT", "The front desk will confirm the cancellations and can help with the records release when you're ready. Expect a call by Monday.", 103],
    ]),
  },
  {
    id: "c-014",
    receivedAt: "2026-08-22T16:25:00-04:00",
    practiceId: "practice-oakhill",
    callerName: "Caleb Whitmore",
    callerPhone: "(555) 121-3364",
    reason: "OTHER",
    recapText:
      "Caleb Whitmore asked about office hours, weekend availability at Oak Hill, and current booking lead times. All answered from the knowledge bank.",
    outcome: "ANSWERED_FROM_KNOWLEDGE",
    nextStep: "None — question fully answered from the knowledge bank.",
    transcript: turns([
      ["AGENT", GREETING, 0],
      ["CALLER", "What time are you guys open?", 4],
      ["AGENT", "Gracent Cares locations are open Monday through Friday, 8 AM to 6 PM. This after-hours line takes messages and answers common questions outside those hours.", 8, "org-kb-003"],
      ["CALLER", "Are you open at all on the weekend?", 22],
      ["AGENT", "Oak Hill runs a limited Saturday morning clinic twice a month for speech therapy follow-ups. Otherwise weekends are covered by this line only.", 28, "prac-oakhill-kb-002"],
      ["CALLER", "Okay. And how far out are you booking right now?", 43],
      ["AGENT", "Initial evaluations are typically two to four weeks out. Cancellation slots open sooner, and the front desk keeps a short-notice list.", 50, "org-kb-009"],
      ["CALLER", "Alright, I'll call Monday. Thanks.", 64],
      ["AGENT", "Happy to help. Have a good evening.", 70],
    ]),
  },

  // ---------- Sunday 2026-08-23 (weekend) ----------
  {
    id: "c-015",
    receivedAt: "2026-08-23T11:05:00-04:00",
    practiceId: "practice-oakhill",
    callerName: "Wyatt Holloway",
    callerPhone: "(555) 130-9925",
    reason: "INSURANCE_ACCEPTANCE",
    recapText:
      "Wyatt Holloway asked whether Gracent Cares accepts Cigna. No payer acceptance is in the knowledge bank at either tier — collected details for a staff callback rather than guessing. Teletherapy and referral questions were answered from the knowledge bank.",
    outcome: "FOLLOW_UP_NEEDED",
    nextStep: "Insurance team to confirm Cigna acceptance and call Wyatt back.",
    followUpTask: {
      description: "Confirm whether Cigna is accepted and call Wyatt Holloway back.",
      patientInfoCollected: [
        "Caller: Wyatt Holloway (prospective patient)",
        "Insurance: Cigna (plan type unknown)",
        "Interested in: speech therapy",
        "Lives roughly 40 minutes out — interested in teletherapy",
      ],
      callbackNumber: "(555) 130-9925",
      dueBy: "2026-08-24T11:00:00-04:00",
      status: "PENDING",
    },
    transcript: turns([
      ["AGENT", GREETING, 0],
      ["CALLER", "Do you take Cigna?", 4],
      ["AGENT", "I'm not sure — which plans we accept isn't something I can confirm on this line, so rather than guess I'll have our insurance team check and call you back. Can I get your name and number?", 8],
      ["CALLER", "Wyatt Holloway, 555-130-9925. It's for speech therapy.", 20],
      ["AGENT", "Thank you. That goes for any plan, not just Cigna — coverage is something the team checks against your specific plan, so they'll get back to you with a real answer.", 30],
      ["CALLER", "Understood. Is speech something you'd do online? I'm about forty minutes out.", 50],
      ["AGENT", "Speech therapy is offered both in person and by teletherapy, so the distance doesn't have to rule it out.", 60, "org-kb-006"],
      ["CALLER", "That helps. Do I need a referral?", 73],
      ["AGENT", "If it's funded through insurance, yes — a physician referral is required for speech therapy. Private-pay evaluations don't need one.", 78, "org-kb-007"],
      ["CALLER", "I'll ask my doctor this week.", 93],
      ["AGENT", "Thanks Wyatt — expect a call about the Cigna question within a few hours of the office opening Monday.", 97],
    ]),
  },
  {
    id: "c-016",
    receivedAt: "2026-08-23T15:30:00-04:00",
    practiceId: "practice-riverside",
    callerName: "Isabel Thorn",
    callerPhone: "(555) 142-6613",
    reason: "RECORDS_FORMS",
    recapText:
      "Isabel Thorn asked how to request a copy of her son's therapy records. Directed to the records-release process from the organization knowledge bank; delivery format left to the front desk.",
    outcome: "ANSWERED_FROM_KNOWLEDGE",
    nextStep: "None — question fully answered from the knowledge bank.",
    transcript: turns([
      ["AGENT", GREETING, 0],
      ["CALLER", "How do I get a copy of my son's therapy records?", 5],
      ["AGENT", "Records requests go through the front desk with a signed release form — you can download the form from the patient portal or pick one up at any location. Processing usually takes 5 to 7 business days.", 11, "org-kb-004"],
      ["CALLER", "Does it matter which location? We've been to Riverside but he saw someone at Oak Hill once.", 28],
      ["AGENT", "Either location can take the request — the release covers his record across Gracent Cares.", 39],
      ["CALLER", "Good. Can they email it, or is it paper only?", 50],
      ["AGENT", "That's not something I can confirm from here, so the front desk will let you know when they process the release.", 57],
      ["CALLER", "Okay. And they're open tomorrow?", 70],
      ["AGENT", "Yes — Monday through Friday, 8 AM to 6 PM.", 75, "org-kb-003"],
      ["CALLER", "Great, I'll grab the form from the portal.", 84],
      ["AGENT", "Sounds good. Anything else I can help with?", 89],
      ["CALLER", "No, that's everything.", 93],
    ]),
  },

  // ---------- Sunday 2026-08-23 (continued) ----------
  {
    id: "c-017",
    receivedAt: "2026-08-23T16:45:00-04:00",
    practiceId: "practice-riverside",
    callerName: "Priya Kapoor",
    callerPhone: "(555) 155-7724",
    patientName: "Arjun Kapoor",
    callerRelationship: "Mother",
    reason: "SCHEDULING",
    recapText:
      "Priya Kapoor called to schedule an appointment for her son Arjun (age 6) at Riverside. The assistant identified this as a New Patient Consultation based on the configured appointment types and booked a 30-minute Tuesday afternoon slot.",
    outcome: "APPOINTMENT_SCHEDULED",
    nextStep: "Appointment confirmed — no staff action required.",
    appointmentType: "New Patient Consultation",
    transcript: [
      ...turns([
        ["AGENT", GREETING, 0],
        ["CALLER", "Hi, I'd like to set up an appointment for my son Arjun with one of your doctors at Riverside. He's six and we're just looking to get started.", 6],
      ]),
      {
        speaker: "AGENT",
        text: "Since you're just getting started, it sounds like a New Patient Consultation would be the best fit. That's a 30-minute introductory appointment where the doctor reviews Arjun's history, does an initial assessment, and talks through a plan with you.",
        offsetSeconds: 14,
        appointmentTypeCited: {
          name: "New Patient Consultation",
          description: "First-time introductory appointment with the doctor. Includes medical history review, initial assessment, and treatment planning.",
          durationMinutes: 30,
        },
      },
      ...turns([
        ["CALLER", "That sounds perfect, exactly what we need.", 30],
        ["AGENT", "Great. We serve patients from 18 months through 21 years, so Arjun is right in range. What days and times work best for you?", 36, "org-kb-005"],
        ["CALLER", "Tuesdays or Thursdays in the afternoon would be ideal.", 48],
        ["AGENT", "I have a Tuesday afternoon slot available next week — August 25th at 2:30 PM. Would that work for a 30-minute visit?", 54],
        ["CALLER", "That's perfect, let's do it.", 64],
        ["AGENT", "Done — I've booked Arjun for a New Patient Consultation at Riverside on Tuesday, August 25th at 2:30 PM.", 70],
        ["CALLER", "Wonderful. What should we bring?", 80],
        ["AGENT", "Bring your insurance card, photo ID, any prior evaluation reports, and the completed intake packet. Arriving fifteen minutes early gives the front desk time to verify benefits.", 86, "org-kb-008"],
        ["CALLER", "Got it. Thank you so much!", 100],
        ["AGENT", "You're welcome. To confirm: Arjun Kapoor, New Patient Consultation at Riverside, Tuesday August 25th at 2:30 PM. See you then!", 104],
        ["CALLER", "See you then, bye!", 118],
      ]),
    ],
  },
];
